import { useState } from "react";
import { Search, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { checkRewards } from "@/lib/api";
import { SimTag, timeAgo } from "@/components/shared";

const SAMPLE_ADDRESS = "7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU";

export default function RewardsChecker() {
  const [address, setAddress] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const runCheck = async (addr) => {
    const target = (addr ?? address).trim();
    setError("");
    setResult(null);
    if (!target) {
      setError("Enter a Solana wallet address to simulate.");
      return;
    }
    setLoading(true);
    try {
      const data = await checkRewards(target);
      setResult(data);
      toast.success("Simulation complete — proportional basket allocation calculated (demo).");
    } catch (e) {
      setError(e?.response?.data?.detail ?? "Simulation failed. Try another address.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="rewards-page" className="bg-[#090612] min-h-screen">
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto glass-panel p-8 md:p-12 rounded-3xl border border-[#9945FF]/40 relative">
          <div className="text-center max-w-xl mx-auto mb-8">
            <SimTag>Interactive Simulation</SimTag>
            <h1 className="display-font uppercase text-3xl md:text-4xl text-white mt-3">Simulated Rewards Calculator</h1>
            <p className="text-sm text-[#b5aed0] mt-2">
              Enter any Solana public key to preview how proportional basket distribution would calculate for that wallet.
            </p>
          </div>

          <div className="space-y-4 max-w-xl mx-auto">
            <div>
              <label htmlFor="wallet-input" className="block text-xs uppercase mono-font text-[#9b93b8] mb-2">Solana Wallet Address (Simulated)</label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  id="wallet-input"
                  data-testid="wallet-address-input"
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runCheck()}
                  placeholder="e.g. 7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU"
                  className="flex-1 bg-black/60 border border-white/20 rounded-xl px-4 py-3 text-sm mono-font text-white focus:outline-none focus:border-[#14F195] placeholder:text-[#5d5678]"
                />
                <button
                  data-testid="simulate-claim-btn"
                  onClick={() => runCheck()}
                  disabled={loading}
                  className="px-6 py-3 bg-[#9945FF] hover:bg-[#8025e6] disabled:opacity-50 text-white font-semibold text-xs tracking-wider uppercase rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  <Search size={14} /> {loading ? "Simulating…" : "Simulate Claim"}
                </button>
              </div>
              <button
                data-testid="use-sample-address-btn"
                onClick={() => { setAddress(SAMPLE_ADDRESS); runCheck(SAMPLE_ADDRESS); }}
                className="mt-2 text-[11px] mono-font text-[#00F0FF] hover:text-[#14F195] transition-colors"
              >
                Use a sample address →
              </button>
            </div>

            {error && (
              <div data-testid="rewards-error" className="flex items-center gap-2 p-3 rounded-xl bg-[#ffaa00]/10 border border-[#ffaa00]/30 text-[#ffaa00] text-xs mono-font">
                <AlertTriangle size={14} /> {error}
              </div>
            )}

            {result && (
              <div data-testid="rewards-result" className="space-y-6 d1-up">
                <div className="p-4 rounded-xl bg-black/40 border border-white/10 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-[10px] text-[#9b93b8] uppercase mono-font">Mock Balance</div>
                    <div className="text-sm font-bold text-white mono-font mt-1" data-testid="result-balance">{result.bask_balance.toLocaleString()} $BASK</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#9b93b8] uppercase mono-font">Pool Share</div>
                    <div className="text-sm font-bold text-[#14F195] mono-font mt-1" data-testid="result-pool-share">{result.pool_share_pct}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#9b93b8] uppercase mono-font">Basket Value</div>
                    <div className="text-sm font-bold text-[#00F0FF] mono-font mt-1" data-testid="result-basket-value">~${result.estimated_basket_value_usd.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#9b93b8] uppercase mono-font">Status</div>
                    <div className={`text-sm font-bold mono-font mt-1 flex items-center justify-center gap-1 ${result.eligible ? "text-[#14F195]" : "text-[#ffaa00]"}`} data-testid="result-eligibility">
                      {result.eligible ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                      {result.eligible ? "Eligible (Demo)" : "Not Eligible"}
                    </div>
                  </div>
                </div>

                <div className="p-3 rounded-xl bg-black/40 border border-white/10">
                  <div className="flex justify-between text-[10px] mono-font mb-1.5">
                    <span className="text-[#9b93b8] uppercase">Eligibility threshold: {result.min_threshold.toLocaleString()} $BASK</span>
                    <span className="text-[#00F0FF]">{result.eligibility_progress}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full alloc-bar" style={{ width: `${result.eligibility_progress}%`, background: result.eligible ? "linear-gradient(90deg,#9945FF,#14F195)" : "#ffaa00" }} />
                  </div>
                  <p className="text-[10px] text-[#7a7493] mono-font mt-2">
                    {result.eligible
                      ? "This wallet clears the example snapshot threshold and would receive a proportional allocation (simulated)."
                      : "Below the example threshold — this wallet would not receive an allocation in this simulated batch."}
                  </p>
                </div>

                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-white mono-font mb-3">Estimated Basket Allocations</h3>
                  <div className="space-y-2" data-testid="result-allocations">
                    {result.allocations.map((a) => (
                      <div key={a.symbol} className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/5">
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-[10px] mono-font"
                            style={{ background: `${a.color}22`, border: `1px solid ${a.color}66`, color: a.color }}>
                            {a.symbol}
                          </div>
                          <span className="text-sm text-white">{a.name}</span>
                        </div>
                        <div className="text-right mono-font">
                          <div className="text-xs text-white font-bold">{a.amount.toLocaleString()} {a.symbol}</div>
                          <div className="text-[10px] text-[#14F195]">~${a.value_usd.toLocaleString()} (Demo)</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-white mono-font mb-3">Simulated Rewards History</h3>
                  <div className="space-y-2" data-testid="result-history">
                    {result.history.map((h, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-black/40 border border-white/5 mono-font text-xs">
                        <div>
                          <span className="text-white">Batch #{h.batch}</span>
                          <span className="text-[#9b93b8] ml-3">{h.assets.join(" + ")}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-[#14F195] font-bold">+${h.value_usd}</div>
                          <div className="text-[10px] text-[#7a7493]">{timeAgo(h.timestamp)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <p className="text-[11px] text-center text-[#ffaa00] mono-font mt-3" data-testid="rewards-disclaimer">
              * This interface is a pre-launch simulation model. No real wallet connection, no real rewards, and no financial transactions occur.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
